<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="750px">
      <template v-if="loading">
        <v-skeleton-loader type="article, actions"></v-skeleton-loader>
      </template>
      <v-card v-if="!loading">
        <v-card-title>
          <v-panel>
            <span style="font-size: 20px">
              {{
                !data.id
                  ? $t("wkt.admin.messages.edit-window.new_message_title")
                  : $t("wkt.admin.messages.edit-window.edit_message_title")
              }}
            </span>
          </v-panel>
        </v-card-title>
        <v-card-text @wheel="onWheel">
          <v-container>
            <v-form ref="form">
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="creationDate"
                    :label="$t('wkt.admin.messages.edit-window.created')"
                    autocomplete="none"
                    :rules="[validationRules.required]"
                    max="200"
                    disabled
                  />
                </v-col>

                <v-col cols="6">
                  <v-select
                    v-model="data.actionType"
                    :items="actionTypes"
                    item-text="name"
                    item-value="id"
                    :label="$t('wkt.admin.messages.edit-window.action_type_placeholder')"
                    :readonly="isReadOnly"
                  />
                </v-col>

                <v-col cols="12">
                  <TargetManager
                    v-model="targetManagerType"
                    :workplaceIdsAsString="data.workplaces ? data.workplaces.join(',') : ''"
                    :departmentIdsAsString="data.departments ? data.departments.join(',') : ''"
                    :legalEntityIdsAsString="data.legalEntities ? data.legalEntities.join(',') : ''"
                    :projectIdsAsString="data.projects ? data.projects.join(',') : ''"
                    :employeeIdsAsString="data.employees ? data.employees.join(',') : ''"
                    :types="convertedTypes"
                    :filterByProfile="!isEditing"
                    :disabled="isEditing"
                    :readonly="isEditing"
                    @update:workplaceIdsAsString="data.workplaces = $event ? $event.split(',').map(Number) : []"
                    @update:departmentIdsAsString="data.departments = $event ? $event.split(',').map(Number) : []"
                    @update:legalEntityIdsAsString="data.legalEntities = $event ? $event.split(',').map(Number) : []"
                    @update:projectIdsAsString="data.projects = $event ? $event.split(',').map(Number) : []"
                    @update:employeeIdsAsString="data.employees = $event ? $event.split(',').map(Number) : []"
                    :targetLabel="$t('wkt.admin.messages.edit-window.receiver_placeholder')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="data.title"
                    :label="$t('wkt.admin.messages.edit-window.title')"
                    autocomplete="none"
                    :rules="[validationRules.required]"
                    maxLength="200"
                    counter="200"
                    :readonly="isReadOnly"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="data.body"
                    :label="$t('wkt.admin.messages.edit-window.content')"
                    autocomplete="none"
                    auto-grow
                    rows="3"
                    maxLength="2000"
                    counter="2000"
                    :readonly="isReadOnly"
                  />
                </v-col>

                <v-col
                  cols="4"
                  v-if="data.actionType === 2 || data.actionType === 3"
                >
                  <v-text-field
                    v-model="data.action1Title"
                    :label="$t('wkt.admin.messages.edit-window.action_name_1')"
                    :rules="action1TitleRules"
                    autocomplete="none"
                    max="200"
                    :readonly="isReadOnly"
                  />
                </v-col>

                <v-col
                  cols="8"
                  v-if="data.actionType === 2 || data.actionType === 3"
                >
                  <v-text-field
                    v-model="data.action1Target"
                    :label="$t('wkt.admin.messages.edit-window.action_target_1')"
                    :rules="action1TargetRules"
                    autocomplete="none"
                    max="200"
                    :readonly="isReadOnly"
                  />
                </v-col>

                <v-col
                  cols="4"
                  v-if="data.actionType === 2 || data.actionType === 3"
                >
                  <v-text-field
                    v-model="data.action2Title"
                    :label="$t('wkt.admin.messages.edit-window.action_name_2')"
                    autocomplete="none"
                    max="200"
                    :readonly="isReadOnly"
                  />
                </v-col>

                <v-col
                  cols="8"
                  v-if="data.actionType === 2 || data.actionType === 3"
                >
                  <v-text-field
                    v-model="data.action2Target"
                    :label="$t('wkt.admin.messages.edit-window.action_target_2')"
                    :rules="action2TargetRules"
                    autocomplete="none"
                    max="200"
                    :readonly="isReadOnly"
                  />
                </v-col>

                <v-col cols="4" class="pb-0">
                  <v-switch
                    v-model="displayIsSystemMessage"
                    :label="$t('wkt.admin.messages.edit-window.send_notification') "
                    :model-value="true"
                    disabled
                  >
                  </v-switch>
                </v-col>

                <v-col cols="4" class="pb-0">
                  <v-switch
                    v-model="displaySticky"
                    :model-value="true"
                    :label="$t('wkt.admin.messages.edit-window.allow_delete')"
                    :readonly="isReadOnly"
                  >
                  </v-switch>
                </v-col>

                <v-col cols="4" class="pb-0">
                  <v-switch
                    v-model="data.enabled"
                    :label="$t('wkt.admin.messages.edit-window.enabled')"
                    :readonly="isReadOnly"
                  >
                  </v-switch>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn v-if="data.id" color="#ff0000" @click="confirmDelete()" :disabled="isReadOnly">
            <v-icon left color="#ffffff">delete</v-icon>
            <span style="color: #ffffff">{{
              $t("wkt.admin.common.dialog-buttons.delete").toUpperCase()
            }}</span>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="#185EA9"
            text
            @click="onSave()"
            style="font-size: 0.9em; font-weight: 400"
            :disabled="isReadOnly"
            >{{ $t("wkt.admin.common.dialog-buttons.ok").toUpperCase() }}</v-btn
          >
          <v-btn
            text
            @click="showDialog = false"
            style="font-size: 0.9em; font-weight: 400"
            >{{
              $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase()
            }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmDialog
      ref="dlgConfirm"
      :title="$t('wkt.admin.messages.confirm-dialog.title')"
      :message="$t('wkt.admin.messages.confirm-dialog.text')"
      :titleOk="$t('wkt.admin.common.dialog-buttons.delete')"
      :titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      @confirmed="deleteItem()"
    />
  </v-row>
</template>

<script>
import BaseModule from "../../common/BaseModule.js";
import ConfirmDialog from "../../common/ConfirmDialog";
import api from "../../../api/worktime";
import CommonFormatter from "../../common/CommonFormatter.js";
import gwt from "../../../api/gwt";
import TargetManager from "../../common/TargetManager.vue";
import { Constants } from "../../common/Constants";

export default {
  name: "MessageDialog",
  mixins: [BaseModule, CommonFormatter],
  components: {
    ConfirmDialog,TargetManager
  },
  data() {
    return {
      showDialog: false,
      creationDate: null,
      loading: true,
      data: {},
      profile: null, sessionEmployee: null,
    };
  },
  computed: {
    actionTypes() {
      return [
        { id: 0,  name: this.$t("wkt.admin.messages.edit-window.action_type_none") },
        { id: 2, name: this.$t("wkt.admin.messages.edit-window.action_type_external_link") },
        { id: 3, name: this.$t("wkt.admin.messages.edit-window.action_type_poll") },
      ];
    },
    displayIsSystemMessage: {
      get() {
        // Siempre se muestra activo (on)
        return true;
      },
      set() {
        // Siempre se mantiene en false en el payload
        this.data.isSystemMessage = false;
      },
    },
    displaySticky: {
      get() {
        // Si data.sticky es false, retornamos true para que se vea activo
        return !this.data.sticky;
      },
      // eslint-disable-next-line vue/return-in-computed-property
      set(value) {
        // Cuando el usuario interactúa, se invierte el valor y se asigna a data.sticky
        this.data.sticky = !value;
      },
    },
    isEditing() {
      return !!this.data.id;
    },
    targetManagerType: {
      get() {
        switch (this.data.type) {
          case 1: return 0; // Company
          case 2: return 1; // Group
          case 6: return 2; // Project
          case 3: return 3; // Employees
          case 4: return 4; // Team
          default: return 3;
        }
      },
      set(value) {
        switch (value) {
          case 0: this.data.type = 1; break;
          case 1: this.data.type = 2; break;
          case 2: this.data.type = 6; break;
          case 3: this.data.type = 3; break;
          case 4: this.data.type = 4; break;
          default: this.data.type = 3;
        }
      }
    },
    convertedTypes() {
      return [
        { id: 0, name: this.$t("wkt.admin.messages.edit-window.receiver_company") },
        { id: 1, name: this.$t("wkt.admin.messages.edit-window.receiver_group") },
        { id: 2, name: this.$t("wkt.admin.messages.edit-window.receiver_project") },
        { id: 3, name: this.$t("wkt.admin.messages.edit-window.receiver_employees") },
        { id: 4, name: this.$t("wkt.admin.messages.edit-window.receiver_myteam") },
      ];
    },
    action1TitleRules() {
      // Si es de enlace externo o consulta, se requiere el título
      if (this.data.actionType === 2 || this.data.actionType === 3) {
        return [this.validationRules.required];
      }
      return [];
    },
    action1TargetRules() {
      // Si es de enlace externo, el target es obligatorio y debe ser una URL válida
      if (this.data.actionType === 2) {
        return [this.validationRules.required, this.validationRules.validateUrl];
      }
      // Si es de consulta, el target es obligatorio pero sin validación de URL
      if (this.data.actionType === 3) {
        return [this.validationRules.required];
      }
      return [];
    },
    action2TargetRules() {
      // Si se ha rellenado el título de la segunda acción y es de enlace externo,
      // el target es obligatorio y debe ser una URL válida
      if (
        this.data.actionType === 2 &&
        this.data.action2Title &&
        this.data.action2Title.trim() !== ""
      ) {
        return [this.validationRules.required, this.validationRules.validateUrl];
      }
      // Si se ha rellenado el título de la segunda acción y es de consulta,
      // el target es obligatorio, pero sin validación de URL
      if (
        this.data.actionType === 3 &&
        this.data.action2Title &&
        this.data.action2Title.trim() !== ""
      ) {
        return [this.validationRules.required];
      }
      return [];
    },
    sessionModuleMessages() {
      return this.profile?.moduleMessages;
    },
    sessionUserIsSuperAdmin() {
      return this.sessionEmployee?.employee?.isSuperAdmin === true;
    },
    isReadOnly() {
      return (
        this.sessionModuleMessages === Constants.ALLOW_READ_ONLY &&
        !this.sessionUserIsSuperAdmin
      );
    },
  },
  methods: {
    // eslint-disable-next-line no-unused-vars
    onWheel(event) {
      event.stopPropagation();
    },
    open(action, payload) {
      this.showDialog = true;

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      // Carga de datos auxiliares
      this.sessionEmployee = gwt.getSessionInformation();
      this.profile = gwt.getSessionProfile();

      // Estamos creando un elemento
      if (!payload) {
        this.data = {
          companyId: null,
          creationDate: new Date(),
          actionType: 0,
          type: 1,
          isSystemMessage: null, // switch de notificación activado por defecto
          sticky: false, // activado por defecto (se podrá desactivar)
          enabled: true, // activado por defecto (se podrá desactivar)
          id: null,
          action1Title: "",
          action1Target: "",
          action2Title: "",
          action2Target: "",
          workplaces: [],
          departments: [],
          legalEntities: [],
          projects: [],
          employees: [],
        };
        this.creationDate = this.formatDateTime(new Date());
        this.loading = false;
        return;
      }

      this.loading = true;
      api.getMessageById(payload,
        (res) => {
          this.creationDate = this.data && this.data.creationDate ? this.formatDateTime(this.data.creationDate) : this.formatDateTime(new Date());
          this.data = res;
          this.creationDate = this.data && this.data.creationDate
              ? this.formatDateTime(this.data.creationDate)
              : this.formatDateTime(new Date());
          
          // Otros procesos (por ejemplo, transformar arrays, etc.)
          this.data.workplaces = this.getWorkplaceIds();
          this.data.departments = this.getDepartmentsIds();
          this.data.legalEntities = this.getLegalEntityIds();
          this.data.projects = this.getProjectIds();
          this.data.employees = this.getEmployeeIds();

          this.loading = false;
        },
        (error) => {
          this.$emit("show-alert", { text: error, color: "error", timeout: 3000 });
          this.loading = false;
        }
      );
    },
    onSave() {
        if (!this.$refs.form.validate()) {
            return;
        }

        const payload = { ...this.data };

        const arrayFields = ['workplaces', 'departments', 'legalEntities', 'projects', 'employees'];
        arrayFields.forEach(field => {
            if (Array.isArray(payload[field])) {
                payload[field] = payload[field].length > 0 ? payload[field].join(',') : null;
            } else {
                payload[field] = null;
            }
        });

        if (payload.creationDate instanceof Date) {
            payload.creationDate = payload.creationDate.getTime();
        } else {
            payload.creationDate = new Date(payload.creationDate).getTime();
        }

        const me = gwt.getSessionInformation().employee;
        payload.companyId = me.companyId || null;
        if (this.isEditing) {
          payload.senderId = this.data.senderId;
        } else {
          payload.senderId = me.id;
        }
        payload.isSystemMessage = null;
        payload.pictureUrl = null;
        payload.isPrivateMessage = false;

        this.loading = true;

        api.sendMessage(payload,
            () => {
                this.loading = false;
                this.$emit("refresh");
                this.showDialog = false;
            },
            (error) => {
                this.$log.error(error);
                this.loading = false;
                this.$emit("show-alert", { text: error, color: "error", timeout: 3000 });
            }
        );
    },
    getWorkplaceIds() {
      let ids = [];
      if (!this.data.workplaces) {
        return ids;
      }
      let workplaceIds = this.data.workplaces.split(",");
      for (let workplaceId of workplaceIds) {
        ids.push(Number(workplaceId));
      }
      return ids;
    },
    getDepartmentsIds() {
      let ids = [];
      if (!this.data.departments) {
        return ids;
      }
      let departmentIds = this.data.departments.split(",");
      for (let departmentId of departmentIds) {
        ids.push(Number(departmentId));
      }
      return ids;
    },
    getProjectIds() {
      let ids = [];
      if (!this.data.projects) {
        return ids;
      }
      let projectIds = this.data.projects.split(",");
      for (let projectId of projectIds) {
        ids.push(Number(projectId));
      }
      return ids;
    },
    getLegalEntityIds() {
      let ids = [];
      if (!this.data.legalEntities) {
        return ids;
      }
      let legalEntityIds = this.data.legalEntities.split(",");
      for (let legalEntityId of legalEntityIds) {
        ids.push(Number(legalEntityId));
      }
      return ids;
    },
    getEmployeeIds() {
      if (!this.data.employees) {
        return [];
      }
      if (typeof this.data.employees === 'string') {
        return [...new Set(this.data.employees.split(",").map(Number))]; // Ensure uniqueness
      }
      if (Array.isArray(this.data.employees)) {
        return [...new Set(this.data.employees.map(employee => employee.id || employee))];
      }
      return [];
    },
    confirmDelete() {
      this.$refs.dlgConfirm.confirm(this.data);
    },
    deleteItem() {
      this.loading = true;

      api.deleteMessage(this.data.id,
        (res) => {
          this.showDialog = false;
          this.data = res;
          this.loading = false;
          this.$emit("refresh");
        },
        (error) => {
          this.loading = false;
          this.$emit("show-alert", { text: error, color: "error", timeout: 3000 });
        }
      );
    },
  },
};
</script>
